/**
 * Dotplot — Tufte's preferred 1-D distribution display.
 *
 * Each item is a single dot positioned along a single axis. Dots stack
 * (visual jitter or piling) when multiple values share the same x, so
 * cluster density is visible at a glance.
 *
 * Use cases:
 *   - whole delegation's stance on a bill (every member is a dot;
 *     x = stance level on a 7-point scale)
 *   - donation amounts in a quarter (x = $ amount, log-scaled)
 *   - cosponsor accumulation timeline (x = weeks since introduction)
 *   - member alignment scores on an issue (x = alignment_pct)
 *
 * Why not a histogram: histograms hide the individual data points behind
 * binning decisions. A dotplot shows every observation. With small
 * samples (≤ 200 dots), you see the SHAPE and the OUTLIERS at the same
 * time — histograms hide both.
 *
 * Why not a bar chart: bars also hide individual points and add
 * non-data-ink (the bar's vertical extent). A dot is one ink-stroke per
 * data point, period.
 */

import React from "react";
// React 19 types no longer declare a global JSX namespace.
import type { JSX } from "react";

export interface DotplotPoint {
  /** Position along the axis. */
  x: number;
  /** Optional label rendered on hover (browser native title). */
  label?: string;
  /** Optional fill color. Default currentColor. */
  color?: string;
  /** Optional category — points with the same category render in the
   *  same y-row when categorical=true (small-multiples-style). */
  category?: string;
}

export interface DotplotProps {
  points: DotplotPoint[];
  /** Pixel width. Default 320. */
  width?: number;
  /** Pixel height. Default 60. */
  height?: number;
  /**
   * Axis bounds. Defaults to data min/max if omitted.
   * Pass explicit bounds when you want multiple dotplots to share a
   * common scale (e.g. small multiples by issue).
   */
  domain?: [number, number];
  /** Optional axis tick positions to label below. */
  ticks?: Array<{ x: number; label: string }>;
  /**
   * If true, group points by category into vertical rows (one row per
   * distinct category). Otherwise all points share a single row, dots
   * stacking vertically when they overlap horizontally.
   */
  categorical?: boolean;
  /** Dot radius in pixels. Default 3. */
  dotRadius?: number;
  /** Optional accessible label. */
  label?: string;
}

const DEFAULT_PALETTE = ["currentColor"];

export function Dotplot({
  points,
  width = 320,
  height = 60,
  domain,
  ticks,
  categorical = false,
  dotRadius = 3,
  label,
}: DotplotProps): JSX.Element | null {
  if (!points || points.length === 0) return null;

  const xs = points.map((p) => p.x);
  const lo = domain?.[0] ?? Math.min(...xs);
  const hi = domain?.[1] ?? Math.max(...xs);
  const scale = Math.max(1, Math.abs(lo), Math.abs(hi));
  const range = hi / scale - lo / scale || 1;

  const padX = 8;
  const padTop = 6;
  const padBottom = ticks && ticks.length > 0 ? 18 : 6;
  const innerW = width - padX * 2;
  const innerH = height - padTop - padBottom;

  const xPx = (x: number): number => padX + ((x / scale - lo / scale) / range) * innerW;

  // Categorical: group by category → one row per distinct category.
  // Non-categorical: simple jitter — count how many other points share
  // the same x bucket and stack them vertically.
  const renderItems: Array<{ cx: number; cy: number; color?: string; title?: string }> = [];

  if (categorical) {
    const cats = Array.from(new Set(points.map((p) => p.category ?? "")));
    const rowH = innerH / Math.max(1, cats.length);
    for (const p of points) {
      const rowIdx = cats.indexOf(p.category ?? "");
      renderItems.push({
        cx: xPx(p.x),
        cy: padTop + rowIdx * rowH + rowH / 2,
        color: p.color,
        title: p.label,
      });
    }
  } else {
    // Bucket points by their pixel x (rounded) and stack vertically
    // within each bucket so duplicates pile up.
    const buckets = new Map<number, number>();
    const bucketSize = dotRadius * 2 + 1;
    for (const p of points) {
      const cx = Math.round(xPx(p.x) / bucketSize) * bucketSize;
      const stack = buckets.get(cx) ?? 0;
      buckets.set(cx, stack + 1);
      renderItems.push({
        cx: cx,
        cy: padTop + innerH - dotRadius - stack * (dotRadius * 2 + 1),
        color: p.color,
        title: p.label,
      });
    }
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label ?? `Dotplot of ${points.length} points`}
    >
      {/* Faint axis line — the only "decoration" we allow, because it
          anchors the visual scale. */}
      <line
        x1={padX}
        x2={width - padX}
        y1={height - padBottom}
        y2={height - padBottom}
        stroke="currentColor"
        strokeWidth={0.5}
        opacity={0.3}
      />
      {ticks?.map((t) => (
        <g key={t.x}>
          <line
            x1={xPx(t.x)}
            x2={xPx(t.x)}
            y1={height - padBottom - 2}
            y2={height - padBottom + 2}
            stroke="currentColor"
            strokeWidth={0.5}
            opacity={0.4}
          />
          <text
            x={xPx(t.x)}
            y={height - padBottom + 12}
            textAnchor="middle"
            fontSize={10}
            fill="currentColor"
            opacity={0.7}
          >
            {t.label}
          </text>
        </g>
      ))}
      {renderItems.map((it, i) => (
        <circle
          key={i}
          cx={it.cx}
          cy={it.cy}
          r={dotRadius}
          fill={it.color ?? DEFAULT_PALETTE[0]}
          opacity={0.85}
        >
          {it.title && <title>{it.title}</title>}
        </circle>
      ))}
    </svg>
  );
}
